import { Alert, Button, Card, DatePicker, Form, Input, Select, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationEnrollmentSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type LeaveRow = {
  id: string;
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  start_date: string;
  end_date: string;
  reason_category: string;
  supporting_ref?: string | null;
  status: string;
  record_version: number;
};

const REASON_CATEGORIES = [
  'Medical',
  'Family emergency',
  'Travel',
  'Religious observance',
  'Sports / competition',
  'Other',
];

function asList<T>(data: unknown): T[] {
  return Array.isArray(data) ? (data as T[]) : [];
}

export default function StudentLeavePage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState<string | undefined>();
  const [form] = Form.useForm();

  const leaves = useQuery({
    queryKey: ['education-leaves'],
    queryFn: async () => asList<LeaveRow>((await api.get('/education/attendance/leaves')).data),
  });

  const onError = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));

  const create = useMutation({
    mutationFn: (values: {
      student_id: string;
      enrollment_id: string;
      start_date: dayjs.Dayjs;
      end_date: dayjs.Dayjs;
      reason_category: string;
      supporting_ref?: string;
    }) =>
      api.post('/education/attendance/leaves', {
        student_id: values.student_id,
        enrollment_id: values.enrollment_id,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date.format('YYYY-MM-DD'),
        reason_category: values.reason_category,
        supporting_ref: values.supporting_ref,
      }),
    onSuccess: () => {
      message.success(t('leaveDrafted', { defaultValue: 'Leave drafted' }));
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['education-leaves'] });
    },
    onError,
  });

  const submit = useMutation({
    mutationFn: (row: LeaveRow) =>
      api.post(`/education/attendance/leaves/${row.id}/submit`, { record_version: row.record_version }),
    onSuccess: () => {
      message.success(t('leaveSubmitted', { defaultValue: 'Leave submitted for approval' }));
      queryClient.invalidateQueries({ queryKey: ['education-leaves'] });
    },
    onError,
  });

  const reconcile = useMutation({
    mutationFn: (row: LeaveRow) => api.post(`/education/attendance/leaves/${row.id}/reconcile`),
    onSuccess: () => {
      message.success(t('leaveReconciled', { defaultValue: 'Leave reconciled to attendance' }));
      queryClient.invalidateQueries({ queryKey: ['education-leaves'] });
    },
    onError,
  });

  return (
    <EducationPageChrome
      title={t('studentLeave')}
      description={t('studentLeaveDetailIntro', {
        defaultValue:
          'Student leave is not employee leave. Draft a request, submit for school approval, then reconcile approved leave against attendance registers.',
      })}
      tags={<Tag>{(leaves.data ?? []).length}</Tag>}
      extra={
        <Button onClick={() => navigate(`/education/attendance?${params.toString()}`)}>
          {t('backToAttendance')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message={t('leaveWorkflowHint', {
            defaultValue: 'Draft → Submit (approval) → after approved, Reconcile marks absences as excused where configured.',
          })}
        />
        <Card size="small" title={t('leaveRegister', { defaultValue: 'Leave register' })}>
          <Table
            rowKey="id"
            size="small"
            loading={leaves.isLoading}
            dataSource={leaves.data ?? []}
            pagination={{ pageSize: 15 }}
            columns={[
              {
                title: t('student'),
                key: 'student',
                render: (_: unknown, row: LeaveRow) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`.trim() || '—',
              },
              { title: t('startDate'), dataIndex: 'start_date', width: 120 },
              { title: t('endDate'), dataIndex: 'end_date', width: 120 },
              { title: t('reason'), dataIndex: 'reason_category', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (status: string) => <Tag>{status}</Tag>,
              },
              {
                title: t('actions', { defaultValue: 'Actions' }),
                key: 'actions',
                width: 200,
                render: (_: unknown, row: LeaveRow) => (
                  <Space>
                    {row.status === 'draft' ? (
                      <Button size="small" type="primary" loading={submit.isPending} onClick={() => submit.mutate(row)}>
                        {t('submit', { defaultValue: 'Submit' })}
                      </Button>
                    ) : null}
                    {row.status === 'approved' ? (
                      <Button size="small" loading={reconcile.isPending} onClick={() => reconcile.mutate(row)}>
                        {t('reconcile', { defaultValue: 'Reconcile' })}
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('newLeave', { defaultValue: 'New leave request' })}>
          <Form form={form} layout="vertical" onFinish={(values) => create.mutate(values)} style={{ maxWidth: 520 }}>
            <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
              <EducationStudentSelect
                onChange={(value) => {
                  setStudentId(value);
                  form.setFieldValue('enrollment_id', undefined);
                }}
              />
            </Form.Item>
            <Form.Item name="enrollment_id" label={t('enrollmentNo')} rules={[{ required: true }]}>
              <EducationEnrollmentSelect studentId={studentId} />
            </Form.Item>
            <Form.Item name="start_date" label={t('startDate')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
            <Form.Item name="end_date" label={t('endDate')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
            <Form.Item name="reason_category" label={t('reason')} rules={[{ required: true }]}>
              <Select options={REASON_CATEGORIES.map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Form.Item name="supporting_ref" label={t('supportingRef')}>
              <Input />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              {t('save')}
            </Button>
          </Form>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
